import React, { useState, useRef } from "react";
import { Upload, FileSpreadsheet, Download, AlertCircle, CheckCircle, Loader2, X, Info, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useAuth } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const InventoryUpload = () => {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [validating, setValidating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(true);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [template, setTemplate] = useState(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const ext = selectedFile.name.split('.').pop().toLowerCase();
      if (!['csv', 'xlsx', 'xls'].includes(ext)) {
        toast.error("Please select a CSV or Excel file (.csv, .xlsx, .xls)");
        return;
      }
      setFile(selectedFile);
      setValidationResult(null);
      setUploadResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      const ext = droppedFile.name.split('.').pop().toLowerCase();
      if (!['csv', 'xlsx', 'xls'].includes(ext)) {
        toast.error("Please select a CSV or Excel file (.csv, .xlsx, .xls)");
        return;
      }
      setFile(droppedFile);
      setValidationResult(null);
      setUploadResult(null);
    }
  };

  const handleValidate = async () => {
    if (!file) return;
    
    setValidating(true);
    setValidationResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/admin/inventory/validate`,
        formData,
        {
          withCredentials: true,
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setValidationResult(response.data);
      if (response.data.valid) {
        toast.success("File validated successfully!");
      } else {
        toast.error("Validation found some issues");
      }
    } catch (error) {
      console.error("Validation error:", error);
      toast.error(error.response?.data?.detail || "Failed to validate file");
    } finally {
      setValidating(false);
    }
  };

  const handleUpload = async () => {
    if (!file || !validationResult?.valid) return;
    
    setUploading(true);
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/admin/inventory/upload`,
        formData,
        {
          params: { update_existing: updateExisting },
          withCredentials: true,
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setUploadResult(response.data);
      toast.success(`Import complete: ${response.data.created} created, ${response.data.updated} updated`);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload inventory");
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/inventory/export`,
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      
      // Convert to CSV
      const data = response.data.products;
      if (data.length === 0) {
        toast.info("No products to export");
        return;
      }
      
      const headers = Object.keys(data[0]);
      const csv = [
        headers.join(','),
        ...data.map(row => headers.map(h => `"${String(row[h] || '').replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${data.length} products`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export inventory");
    }
  };

  const fetchTemplate = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/inventory/template`,
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      setTemplate(response.data);
      setTemplateDialogOpen(true);
    } catch (error) {
      console.error("Template error:", error);
      toast.error("Failed to fetch template info");
    }
  };

  const downloadSampleCSV = () => {
    if (!template) return;
    
    const headers = [...template.required_columns, ...template.optional_columns];
    const sampleRow = headers.map(h => template.sample_row[h] || '');
    
    const csv = [
      headers.join(','),
      sampleRow.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'inventory_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Template downloaded");
  };

  const clearFile = () => {
    setFile(null);
    setValidationResult(null);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6" data-testid="inventory-upload">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-bold text-gray-900 uppercase">
            Inventory Upload
          </h3>
          <p className="text-gray-500 text-sm">
            Upload product catalog via CSV or Excel spreadsheet
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchTemplate} className="border-gray-200">
            <Info className="w-4 h-4 mr-2" />
            View Template
          </Button>
          <Button variant="outline" onClick={handleExport} className="border-gray-200">
            <Download className="w-4 h-4 mr-2" />
            Export Current
          </Button>
        </div>
      </div>

      {/* Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          file ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-primary'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileSelect}
          className="hidden"
          id="inventory-file"
        />
        
        {file ? (
          <div className="flex items-center justify-center gap-4">
            <FileSpreadsheet className="w-12 h-12 text-green-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900">{file.name}</p>
              <p className="text-gray-500 text-sm">{(file.size / 1024).toFixed(1)} KB</p>
            </div>
            <Button variant="ghost" size="sm" onClick={clearFile} className="text-gray-400 hover:text-red-500">
              <X className="w-5 h-5" />
            </Button>
          </div>
        ) : (
          <>
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">
              Drag and drop your spreadsheet here, or{' '}
              <label htmlFor="inventory-file" className="text-primary cursor-pointer hover:underline">
                browse
              </label>
            </p>
            <p className="text-gray-400 text-sm">Supports CSV, XLSX, XLS files</p>
          </>
        )}
      </div>

      {/* Options */}
      {file && (
        <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            <Switch
              id="update-existing"
              checked={updateExisting}
              onCheckedChange={setUpdateExisting}
            />
            <Label htmlFor="update-existing" className="text-gray-700">
              Update existing products (match by SKU)
            </Label>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {file && (
        <div className="flex gap-3">
          <Button
            onClick={handleValidate}
            disabled={validating}
            variant="outline"
            className="border-gray-200"
          >
            {validating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Validating...
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 mr-2" />
                Validate File
              </>
            )}
          </Button>
          
          <Button
            onClick={handleUpload}
            disabled={uploading || !validationResult?.valid}
            className="bg-primary hover:bg-primary/90"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Import Products
              </>
            )}
          </Button>
        </div>
      )}

      {/* Validation Results */}
      {validationResult && (
        <div className={`p-4 rounded-lg border ${
          validationResult.valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center gap-2 mb-3">
            {validationResult.valid ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-800">Validation Passed</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="font-medium text-red-800">Validation Failed</span>
              </>
            )}
          </div>
          
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div className="bg-white p-3 rounded border">
              <p className="text-2xl font-bold text-gray-900">{validationResult.total_rows}</p>
              <p className="text-gray-500 text-sm">Total Rows</p>
            </div>
            <div className="bg-white p-3 rounded border">
              <p className="text-2xl font-bold text-green-600">{validationResult.valid_rows}</p>
              <p className="text-gray-500 text-sm">Valid Rows</p>
            </div>
            <div className="bg-white p-3 rounded border">
              <p className="text-2xl font-bold text-red-600">{validationResult.invalid_rows}</p>
              <p className="text-gray-500 text-sm">Invalid Rows</p>
            </div>
          </div>

          {/* Warnings */}
          {validationResult.warnings?.length > 0 && (
            <div className="mb-4">
              <p className="font-medium text-amber-700 mb-2">Warnings:</p>
              <ul className="text-amber-600 text-sm space-y-1 max-h-32 overflow-y-auto">
                {validationResult.warnings.map((w, i) => (
                  <li key={i}>• {w}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Errors */}
          {validationResult.errors?.length > 0 && (
            <div>
              <p className="font-medium text-red-700 mb-2">Errors:</p>
              <ul className="text-red-600 text-sm space-y-1 max-h-48 overflow-y-auto">
                {validationResult.errors.map((e, i) => (
                  <li key={i}>• {e}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Preview */}
          {validationResult.preview?.length > 0 && (
            <div className="mt-4">
              <p className="font-medium text-gray-700 mb-2">Preview (first 5 rows):</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      {validationResult.columns_found.slice(0, 6).map((col) => (
                        <th key={col} className="px-2 py-1 text-left text-gray-600 font-medium">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {validationResult.preview.map((row, idx) => (
                      <tr key={idx} className="border-t border-gray-100">
                        {validationResult.columns_found.slice(0, 6).map((col) => (
                          <td key={col} className="px-2 py-1 text-gray-900 truncate max-w-[150px]">
                            {String(row[col] || '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upload Results */}
      {uploadResult && (
        <div className="p-4 rounded-lg bg-green-50 border border-green-200">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="font-medium text-green-800">Import Complete</span>
          </div>
          
          <div className="grid md:grid-cols-4 gap-4">
            <div className="bg-white p-3 rounded border">
              <p className="text-2xl font-bold text-gray-900">{uploadResult.total_processed}</p>
              <p className="text-gray-500 text-sm">Total Processed</p>
            </div>
            <div className="bg-white p-3 rounded border">
              <p className="text-2xl font-bold text-green-600">{uploadResult.created}</p>
              <p className="text-gray-500 text-sm">Created</p>
            </div>
            <div className="bg-white p-3 rounded border">
              <p className="text-2xl font-bold text-blue-600">{uploadResult.updated}</p>
              <p className="text-gray-500 text-sm">Updated</p>
            </div>
            <div className="bg-white p-3 rounded border">
              <p className="text-2xl font-bold text-red-600">{uploadResult.errors}</p>
              <p className="text-gray-500 text-sm">Errors</p>
            </div>
          </div>

          {uploadResult.error_messages?.length > 0 && (
            <div className="mt-4">
              <p className="font-medium text-red-700 mb-2">Error Details:</p>
              <ul className="text-red-600 text-sm space-y-1 max-h-32 overflow-y-auto">
                {uploadResult.error_messages.map((e, i) => (
                  <li key={i}>• {e}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900 font-heading text-xl uppercase">
              Spreadsheet Template
            </DialogTitle>
          </DialogHeader>
          
          {template && (
            <div className="space-y-6 mt-4">
              <div className="flex gap-3">
                <Button onClick={downloadSampleCSV} className="bg-primary hover:bg-primary/90">
                  <Download className="w-4 h-4 mr-2" />
                  Download Sample CSV
                </Button>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Required Columns</h4>
                <div className="flex flex-wrap gap-2">
                  {template.required_columns.map((col) => (
                    <span key={col} className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                      {col}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Optional Columns</h4>
                <div className="flex flex-wrap gap-2">
                  {template.optional_columns.map((col) => (
                    <span key={col} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                      {col}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Column Descriptions</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {Object.entries(template.column_descriptions).map(([col, desc]) => (
                    <div key={col} className="flex gap-2 text-sm">
                      <span className="font-mono text-primary min-w-[140px]">{col}:</span>
                      <span className="text-gray-600">{desc}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Sample Row</h4>
                <div className="bg-gray-50 p-3 rounded overflow-x-auto">
                  <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                    {JSON.stringify(template.sample_row, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryUpload;
